import type { ICellRendererParams } from '@mint-lib/ag-grid/community';
import {
  Button,
  CircularLoadingButton,
  TruncateText,
} from '@mint-lib/tw-components';

import {
  isFolderNode,
  isPendingFolder,
} from '../../../../../helpers/knowledge-base/fileSystemNode.js';
import { getFolderIcon } from '../../../../../helpers/knowledge-base/getFolderIcon.js';
import type {
  FileSystemNode,
  FolderNode,
} from '../../../../../types/fileSystem.js';
import { FileIcon } from '../../FileIcon/FileIcon.jsx';
import { FolderNameInput } from '../../FolderSection/FolderNameInput.jsx';

type Props = ICellRendererParams<FileSystemNode> & {
  onOpenFolder: (folder: FolderNode) => void;
  onCommitDraft: (name: string) => boolean;
  onCancelDraft: () => void;
  renamingFolderId: string | null;
  onCommitRename: (id: string, name: string) => boolean;
  onCancelRename: () => void;
  validateName: (name: string, excludeId?: string) => boolean;
  openingFolderId?: string | null;
};

const NameCell = ({
  node,
  data,
  onOpenFolder,
  onCommitDraft,
  onCancelDraft,
  renamingFolderId,
  onCommitRename,
  onCancelRename,
  validateName,
  openingFolderId,
}: Props) => {
  if (node?.rowPinned) {
    const folderData = data as FolderNode;

    return (
      <div className="flex h-full w-full flex-row items-center gap-2">
        <FileIcon type={getFolderIcon(folderData)} />
        <div className="w-full">
          <FolderNameInput
            className="h-7"
            onCommit={onCommitDraft}
            onCancel={onCancelDraft}
            validateName={validateName}
          />
        </div>
      </div>
    );
  }

  if (!data) return null;

  if (isFolderNode(data) && data.id === renamingFolderId) {
    const folderId = data.id;

    return (
      <div className="flex h-full w-full flex-row items-center gap-2">
        <FileIcon type={getFolderIcon(data)} />
        <div className="w-full">
          <FolderNameInput
            className="h-7"
            initialValue={data.name}
            onCommit={(name) => onCommitRename(folderId, name)}
            onCancel={onCancelRename}
            validateName={(name) => validateName(name, folderId)}
          />
        </div>
      </div>
    );
  }

  if (isFolderNode(data) && isPendingFolder(data)) {
    return (
      <div className="flex flex-row items-center gap-2.5">
        <FileIcon type="folder" />
        <TruncateText>{data.name}</TruncateText>
      </div>
    );
  }

  if (isFolderNode(data)) {
    return (
      <div className="flex h-full w-full flex-row items-center gap-2">
        <Button
          variant="plain"
          size="small"
          className="w-full justify-start text-text-primary hover:text-text-primary"
          data-dd-action-name="Knowledge base: open folder"
          onClick={() => onOpenFolder(data)}
        >
          {openingFolderId === data.id ? (
            <CircularLoadingButton
              size="small"
              variant="outlined"
              aria-label="opening-folder"
            />
          ) : (
            <FileIcon type={getFolderIcon(data)} />
          )}
          <TruncateText>{data.name}</TruncateText>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-2.5">
      <FileIcon type={data.fileType} />
      <TruncateText>{data.fileName}</TruncateText>
    </div>
  );
};

export { NameCell };
